/**
 * Showcase demo data — one coherent fictional cast for all Coaching OS marketing
 * showcase components, so every surface tells the same story.
 *
 * Canonical narrative (keep every surface consistent with this):
 * - Coach: Sofia Reyes.
 * - Maya Chen — ~8 sessions in; north star "Build a sustainable consulting
 *   practice"; next session Thursday; two active actions. (The "hero" client.)
 * - Daniel Osei — earlier stage, a couple of sessions in.
 *
 * Plain client-safe data ONLY. Never import src/lib/server, src/lib/crypto,
 * src/lib/queries, or src/routes here — this module is consumed by edge-safe
 * showcase components and would re-couple every one of them.
 *
 * Each surface unit appends its own typed fixture export below, drawing from the
 * canonical cast + facts defined here.
 *
 * Aliases: showcase fixtures, demo data, marketing sample data, fictional cast.
 */

import { GoalStatus, GoalTimeline } from "~/../drizzle/schema";
import type { ActionCardProps } from "~/components/engagement/action-card/action-card-types";
import type { TimelineSessionItem } from "~/components/engagement/coach-session-timeline-view";
import type { CoachEngagementCardData } from "~/components/engagement/engagement-card-coach";
import { EngagementStatus, SessionStatus } from "~/lib/schemas/db-enums";

/** A person shown on a showcase surface. No avatar URL → initials fallback. */
export interface DemoPerson {
  readonly name: string;
  readonly profilePictureUrl?: string;
}

export const SHOWCASE_COACH: DemoPerson = {
  name: "Sofia Reyes",
};

export const SHOWCASE_CLIENTS = {
  maya: { name: "Maya Chen" },
  daniel: { name: "Daniel Osei" },
} as const satisfies Record<string, DemoPerson>;

/**
 * Canonical narrative facts the surfaces draw from, so e.g. Maya's session count
 * on the timeline matches her progress on the client card.
 */
export const SHOWCASE_CANON = {
  maya: {
    sessionsCompleted: 8,
    sessionsTotal: 12,
    northStar: "Build a sustainable consulting practice",
    nextSessionLabel: "Thursday, 2:00 PM",
  },
  daniel: {
    sessionsCompleted: 2,
    sessionsTotal: 10,
    northStar: "Find a role that fits my values",
    nextSessionLabel: "Next Tuesday, 10:00 AM",
  },
} as const;

const HOUR_MS = 60 * 60 * 1000;

/**
 * The showcase clock. All clock-derived fixtures are FACTORY functions built on
 * this, never top-level consts: on Cloudflare Workers, `Date.now()` during
 * module evaluation (isolate startup) returns 0, so module-scope date math
 * would SSR "Jan 1, 1970" to crawlers. Consumers call the factories at render
 * time (inside a request) and memoize per mount.
 *
 * Floored to the top of the hour so the server-rendered HTML and the client's
 * hydration pass compute identical timestamps unless they straddle an hour
 * boundary — collapsing the per-millisecond drift that would otherwise make
 * minute-granular labels mismatch on (almost) every hydration.
 */
function showcaseNow(): number {
  return Math.floor(Date.now() / HOUR_MS) * HOUR_MS;
}

/** Days from now, as an ISO string — keeps showcase "in N days" labels fresh. */
function daysFromNowIso(days: number): string {
  return new Date(showcaseNow() + days * 24 * HOUR_MS).toISOString();
}

/** Days from now, as a Date — for fixtures whose consumers take Date props. */
function daysFromNow(days: number): Date {
  return new Date(showcaseNow() + days * 24 * HOUR_MS);
}

/**
 * Sofia's client roster as it appears on the coach client dashboard — fed to
 * `CoachEngagementCardView` inside `CoachClientsShowcase`. Drawn from the
 * canonical cast: Maya is mid-journey with an upcoming session and a recap;
 * Daniel is earlier-stage with no session yet booked (so his card shows the
 * "No upcoming session" attention signal — the surface's signature reveal).
 */
export const getShowcaseCoachClients =
  (): readonly CoachEngagementCardData[] => [
    {
      engagementId: "showcase-maya",
      client: { name: SHOWCASE_CLIENTS.maya.name, avatar: null },
      statusChip: EngagementStatus.ACTIVE,
      mainCoachingGoal: SHOWCASE_CANON.maya.northStar,
      lastSessionSummary:
        "Mapped the first three offers and picked a niche to lead with. Maya's drafting outreach to two warm leads before Thursday.",
      nextSession: daysFromNowIso(4),
      renewal: { renewalDueDate: daysFromNowIso(58) },
      progress: {
        sessionsCompleted: SHOWCASE_CANON.maya.sessionsCompleted,
        sessionsTotal: SHOWCASE_CANON.maya.sessionsTotal,
      },
    },
    {
      engagementId: "showcase-daniel",
      client: { name: SHOWCASE_CLIENTS.daniel.name, avatar: null },
      statusChip: EngagementStatus.ACTIVE,
      mainCoachingGoal: SHOWCASE_CANON.daniel.northStar,
      lastSessionSummary:
        "Clarified the values that matter most in his next role and named two industries to explore.",
      nextSession: null,
      renewal: { renewalDueDate: null },
      progress: {
        sessionsCompleted: SHOWCASE_CANON.daniel.sessionsCompleted,
        sessionsTotal: SHOWCASE_CANON.daniel.sessionsTotal,
      },
    },
  ];

/**
 * Maya's session timeline as it appears on the coach session surface — fed to
 * `CoachSessionTimelineView` inside `SessionTimelineShowcase`. Drawn from the
 * canonical cast: ~8 sessions in, an upcoming Thursday session tied to her north
 * star, and a recent history with notes + homework so the signature hover/focus
 * reveal (notes & task counts) has something to surface.
 */
export const getShowcaseTimelineUpcoming =
  (): readonly TimelineSessionItem[] => [
    {
      id: "showcase-session-up-1",
      sessionNumber: 9,
      title: "Pricing the first three offers",
      startAt: daysFromNowIso(4),
      duration: 50,
      status: SessionStatus.SCHEDULED,
      goalDescription: SHOWCASE_CANON.maya.northStar,
      hasMeetingUrl: true,
    },
    {
      id: "showcase-session-up-2",
      sessionNumber: 10,
      title: "Outreach review & momentum check",
      startAt: daysFromNowIso(11),
      duration: 50,
      status: SessionStatus.SCHEDULED,
      goalDescription: "Land two discovery calls",
      hasMeetingUrl: false,
    },
  ];

export const getShowcaseTimelinePast = (): readonly TimelineSessionItem[] => [
  {
    id: "showcase-session-past-1",
    sessionNumber: 8,
    title: "Choosing a niche to lead with",
    startAt: daysFromNowIso(-3),
    duration: 50,
    status: SessionStatus.COMPLETED,
    notes: { coach: 2, client: 1 },
    homeworkCount: 3,
  },
  {
    id: "showcase-session-past-2",
    sessionNumber: 7,
    title: "Mapping the offer ladder",
    startAt: daysFromNowIso(-10),
    duration: 50,
    status: SessionStatus.COMPLETED,
    notes: { coach: 1, client: 0 },
    homeworkCount: 1,
  },
  {
    id: "showcase-session-past-3",
    sessionNumber: 6,
    title: "Defining the ideal client",
    startAt: daysFromNowIso(-17),
    duration: 50,
    status: SessionStatus.COMPLETED,
  },
];

/** Maya's session progress, as shown above the timeline. */
export const SHOWCASE_TIMELINE_PROGRESS = {
  sessionsCompleted: SHOWCASE_CANON.maya.sessionsCompleted,
  sessionsTotal: SHOWCASE_CANON.maya.sessionsTotal,
} as const;

/**
 * Maya's Living Action cards — fed to `ActionCard` inside `ActionShowcase`. One
 * active coach action that leads with its implementation-intention trigger and
 * links to Maya's north star; one completed coach action that became a
 * celebrated win (saved reflection + promoted); and a self-created task (light
 * treatment — no trigger/goal/coach). Rendered full-density with no
 * reflection-write handler, so the inline "how did it go?" input never appears
 * on the public page.
 */
export type ShowcaseAction = Pick<
  ActionCardProps,
  | "title"
  | "status"
  | "deadline"
  | "trigger"
  | "goalLabel"
  | "coachName"
  | "coachAvatarUrl"
  | "kind"
  | "reflection"
  | "promoted"
> & { readonly id: string };

export const getShowcaseActions = (): readonly ShowcaseAction[] => [
  {
    id: "showcase-action-1",
    title: "Outline this week's client offer",
    status: "active",
    deadline: null,
    trigger: "After my Monday standup → outline this week's offer",
    goalLabel: SHOWCASE_CANON.maya.northStar,
    coachName: SHOWCASE_COACH.name,
    coachAvatarUrl: null,
    kind: "coach",
    reflection: null,
    promoted: false,
  },
  {
    id: "showcase-action-2",
    title: "Reach out to two warm leads",
    status: "done",
    deadline: null,
    trigger: null,
    goalLabel: SHOWCASE_CANON.maya.northStar,
    coachName: SHOWCASE_COACH.name,
    coachAvatarUrl: null,
    kind: "coach",
    reflection: "Sent both — one already booked a call for Thursday!",
    promoted: true,
  },
  {
    id: "showcase-action-3",
    title: "Block 30 minutes for focused writing",
    status: "active",
    deadline: daysFromNow(2),
    trigger: null,
    goalLabel: null,
    coachName: null,
    coachAvatarUrl: null,
    kind: "self",
    reflection: null,
    promoted: false,
  },
];

/** One row in the Hearth's "what needs you" strip. */
export interface HearthNeed {
  readonly kind: "homework" | "message" | "checkin";
  readonly text: string;
}

/**
 * The client Hearth (coaching home) as Maya sees it — fed to the hero
 * `ClientWorkspaceShowcase`. A faithful replica of the real Hearth's composition
 * (greeting → next-session cue → north-star → what-needs-you), populated from the
 * canonical cast: addressed to Maya, Sofia present as her coach, her north star
 * standing in for the decrypted goal, an upcoming Thursday session, and two
 * needs (a coach action due, an unread reply from Sofia).
 *
 * `greetingTimeOfDay` is a fixed string (never derived from `useUser`/the clock)
 * so the surface renders deterministically on the public, unauthenticated page.
 */
export interface ShowcaseHearth {
  readonly greetingName: string;
  readonly greetingTimeOfDay: "morning" | "afternoon" | "evening";
  readonly greetingSubline: string;
  readonly coach: DemoPerson;
  readonly northStar: string;
  readonly needs: readonly HearthNeed[];
  readonly nextSession: { readonly title: string; readonly whenLabel: string };
}

export const SHOWCASE_HEARTH: ShowcaseHearth = {
  greetingName: SHOWCASE_CLIENTS.maya.name.split(" ")[0] ?? "there",
  greetingTimeOfDay: "morning",
  greetingSubline: "One session closer to the practice you're building.",
  coach: SHOWCASE_COACH,
  northStar: SHOWCASE_CANON.maya.northStar,
  needs: [
    {
      kind: "homework",
      text: "Outline this week's client offer — due before Thursday",
    },
    {
      kind: "message",
      text: `New reply from ${SHOWCASE_COACH.name.split(" ")[0]}`,
    },
  ],
  nextSession: {
    title: "Pricing the first three offers",
    whenLabel: SHOWCASE_CANON.maya.nextSessionLabel,
  },
} as const;

/**
 * One of Maya's life-vision goals — fed to `GoalsShowcase`. A plaintext stand-in
 * for the real (encrypted) Life Design `Goal` row, carrying only what the goal-card
 * grid renders: the category it belongs to, its title, status + timeline badges,
 * and milestone completion counts (driving the progress bar).
 *
 * `category` is a plain label used both to group cards and to power the
 * signature category-filter interaction.
 */
export interface ShowcaseGoal {
  readonly id: string;
  readonly category: string;
  readonly title: string;
  readonly status: GoalStatus;
  readonly timeline: GoalTimeline;
  readonly milestonesCompleted: number;
  readonly milestonesTotal: number;
}

/**
 * Maya Chen's life vision as it appears on the Goals / Life Design surface —
 * three categories (Career, Health & Energy, Relationships), each with at least
 * one goal, so every category pill filters to a non-empty set and the "All" pill
 * shows the full grid. Drawn from the canonical cast: her north star anchors the
 * Career goals.
 */
/**
 * One message in Sofia ↔ Maya's thread — fed to `MessagingShowcase`, which maps
 * each onto the real kit `ChatBubble`. A plaintext stand-in for the real
 * (encrypted) message row, carrying only what a bubble renders. `fromCoach`
 * distinguishes Sofia's messages from Maya's; the showcase reads the thread from
 * the *client's* inbox, so a coach message is incoming and Maya's own are
 * outgoing.
 */
export interface ShowcaseMessage {
  readonly id: string;
  readonly content: string;
  readonly fromCoach: boolean;
  readonly createdAt: Date;
}

/** Minutes ago, as a Date — keeps the thread's timestamps recent on each load. */
function minutesAgo(minutes: number): Date {
  return new Date(showcaseNow() - minutes * 60 * 1000);
}

/**
 * Sofia ↔ Maya's message thread as Maya sees it in her inbox — a natural
 * coaching exchange mid-journey on her consulting practice, Sofia warm and
 * encouraging. The FINAL message (Sofia's "See you Thursday" reply) is the one
 * the showcase resolves after a looping "typing…" cue, so keep it last and
 * sender = coach.
 */
export const getShowcaseMessages = (): readonly ShowcaseMessage[] => [
  {
    id: "showcase-msg-1",
    content:
      "Sent outreach to both warm leads this morning — one already wants to talk pricing 😅",
    fromCoach: false,
    createdAt: minutesAgo(64),
  },
  {
    id: "showcase-msg-2",
    content:
      "That's huge, Maya! See — the niche you picked is already pulling the right people in.",
    fromCoach: true,
    createdAt: minutesAgo(58),
  },
  {
    id: "showcase-msg-3",
    content:
      "Should I send them the three-offer outline before our call, or wait and walk through it live?",
    fromCoach: false,
    createdAt: minutesAgo(41),
  },
  {
    id: "showcase-msg-4",
    content:
      "Let's walk it through live Thursday — bring the numbers and we'll price it together.",
    fromCoach: true,
    createdAt: minutesAgo(36),
  },
  {
    id: "showcase-msg-5",
    content: "See you Thursday — proud of the progress you're making. 🌱",
    fromCoach: true,
    createdAt: minutesAgo(2),
  },
];

export const SHOWCASE_GOALS: readonly ShowcaseGoal[] = [
  {
    id: "showcase-goal-career-1",
    category: "Career",
    title: SHOWCASE_CANON.maya.northStar,
    status: GoalStatus.IN_PROGRESS,
    timeline: GoalTimeline.YEAR,
    milestonesCompleted: 3,
    milestonesTotal: 6,
  },
  {
    id: "showcase-goal-career-2",
    category: "Career",
    title: "Land three retainer clients",
    status: GoalStatus.IN_PROGRESS,
    timeline: GoalTimeline.QUARTER,
    milestonesCompleted: 1,
    milestonesTotal: 4,
  },
  {
    id: "showcase-goal-health-1",
    category: "Health & Energy",
    title: "Build a sustainable morning routine",
    status: GoalStatus.IN_PROGRESS,
    timeline: GoalTimeline.QUARTER,
    milestonesCompleted: 4,
    milestonesTotal: 5,
  },
  {
    id: "showcase-goal-health-2",
    category: "Health & Energy",
    title: "Run a half marathon",
    status: GoalStatus.PLANNED,
    timeline: GoalTimeline.THREE_YEARS,
    milestonesCompleted: 0,
    milestonesTotal: 8,
  },
  {
    id: "showcase-goal-relationships-1",
    category: "Relationships",
    title: "Protect two evenings a week for family",
    status: GoalStatus.COMPLETED,
    timeline: GoalTimeline.QUARTER,
    milestonesCompleted: 3,
    milestonesTotal: 3,
  },
] as const;
